import React, { useState } from 'react';
import { X } from './icons';
import Colors, { withAlpha } from '../../constants/Colors';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function splitEmailTokens(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map(normalizeEmail)
    .filter(Boolean);
}

type EmailChipsInputProps = {
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

export default function EmailChipsInput({
  emails,
  onChange,
  placeholder = 'Type email and press Enter',
  disabled = false,
}: EmailChipsInputProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addEmails = (rawValues: string[]) => {
    const next = [...emails];
    let added = 0;
    let invalid = 0;

    rawValues.forEach((raw) => {
      const email = normalizeEmail(raw);
      if (!email) return;
      if (!EMAIL_RE.test(email)) {
        invalid += 1;
        return;
      }
      if (!next.includes(email)) {
        next.push(email);
        added += 1;
      }
    });

    if (invalid > 0 && added === 0) {
      setError('Enter a valid email address');
      return false;
    }

    setError(null);
    if (added > 0) onChange(next);
    return true;
  };

  const commitDraft = () => {
    if (!draft.trim()) return;
    if (addEmails(splitEmailTokens(draft))) {
      setDraft('');
    }
  };

  return (
    <div>
      <div
        className="form-input"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '6px',
          minHeight: '42px',
          height: 'auto',
          padding: '6px 10px',
          cursor: disabled ? 'not-allowed' : 'text',
          opacity: disabled ? 0.7 : 1,
        }}
        onClick={(e) => {
          if (disabled) return;
          const input = e.currentTarget.querySelector('input');
          input?.focus();
        }}
      >
        {emails.map((email) => (
          <span
            key={email}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              maxWidth: '100%',
              padding: '4px 8px',
              borderRadius: '999px',
              background: withAlpha(Colors.accent, 0.12),
              color: Colors.text,
              fontSize: '12.5px',
              fontWeight: 600,
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</span>
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${email}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(emails.filter((item) => item !== email));
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  display: 'flex',
                  cursor: 'pointer',
                  color: Colors.textLight,
                }}
              >
                <X size={12} />
              </button>
            )}
          </span>
        ))}
        <input
          type="email"
          value={draft}
          disabled={disabled}
          placeholder={emails.length === 0 ? placeholder : 'Add another email…'}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
              if (draft.trim()) {
                e.preventDefault();
                commitDraft();
              }
            } else if (e.key === 'Backspace' && !draft && emails.length > 0) {
              onChange(emails.slice(0, -1));
            }
          }}
          onBlur={commitDraft}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (!text || (!text.includes(',') && !text.includes(';') && !text.includes(' ') && !text.includes('\n'))) {
              return;
            }
            e.preventDefault();
            if (addEmails(splitEmailTokens(`${draft} ${text}`))) {
              setDraft('');
            }
          }}
          style={{
            flex: '1 1 160px',
            minWidth: '140px',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: '14px',
            color: Colors.text,
            padding: '4px 0',
          }}
        />
      </div>
      {error && (
        <div style={{ marginTop: 6, fontSize: 12, color: Colors.error }}>{error}</div>
      )}
      <div style={{ marginTop: 6, fontSize: 12, color: Colors.textLight }}>
        Press Enter to add.
      </div>
    </div>
  );
}
